import { makeAutoObservable, runInAction } from "mobx";
import ApiStore from "./ApiStore";
import CurrencyExchangeRateStore from "./CurrencyExchangeRateStore";

export type TariffPlans = {
  id: number;
  name: string;
  description: string;
  maxUsers: number;
  maxProjects: number;
  pricePerMonth: number;
  pricePerYear: number;
  isPublic: boolean;
}

type GlobalPricing = {
  basic: { month: number; year: number };
  comfort: { month: number; year: number };
  infinity: { month: number; year: number };
};

const DEFAULT_GLOBAL_PRICING: GlobalPricing = {
  basic: { month: 10, year: 100 },
  comfort: { month: 25, year: 250 },
  infinity: { month: 30, year: 300 },
};

const PLAN_NAME = {
  BASIC: "Basic",
  COMFORT: "Comfort",
  INFINITY: "Infinity",
} as const;

const round2 = (n: number) => Math.round(n * 100) / 100;

class TariffPlansStore {
  tariffPlans: TariffPlans[] = [];
  isLoading = true;
  globalPricing: GlobalPricing = DEFAULT_GLOBAL_PRICING;
  localPricing: GlobalPricing = DEFAULT_GLOBAL_PRICING;
  ApiStore!: ApiStore;
  private сurrencyExchangeRateStore: CurrencyExchangeRateStore;

  constructor(apiStore: ApiStore, сurrencyExchangeRateStore: CurrencyExchangeRateStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.сurrencyExchangeRateStore = сurrencyExchangeRateStore;
  }

  setGlobalPricing = (newGlobalPricing: typeof this.globalPricing) => {
    this.globalPricing = newGlobalPricing;
  };

  private initLocalPricing = async () => {
    await this.сurrencyExchangeRateStore.getLatestCurrencyExchangeRate();
    const m = this.сurrencyExchangeRateStore.unitsPerUsd;
    const g = this.globalPricing;
    this.localPricing = {
      basic: { month: round2(g.basic.month * m), year: round2(g.basic.year * m) },
      comfort: { month: round2(g.comfort.month * m), year: round2(g.comfort.year * m) },
      infinity: { month: round2(g.infinity.month * m), year: round2(g.infinity.year * m) },
    };
  };

  recalcLocalPricing = () => {
    this.initLocalPricing();
  };

  getTariffPlans = async () => {
    this.isLoading = true;
    try {
      const tariffPlans = await this.ApiStore.TariffPlansApiClient.getTariffPlans();

      runInAction(() => {
        this.tariffPlans = tariffPlans;
        this.globalPricing = this.buildGlobalPricing(tariffPlans);
        this.initLocalPricing();
      });
    } catch (e) {
      console.error("Error fetching tariff plans:", e);

      runInAction(() => {
        this.tariffPlans = [];
        this.globalPricing = DEFAULT_GLOBAL_PRICING;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  private buildGlobalPricing = (plans: TariffPlans[]): GlobalPricing => {
    const byName = (name: string) =>
      plans.find((p) => p.name.toLowerCase() === name.toLowerCase());

    const basic = byName(PLAN_NAME.BASIC);
    const comfort = byName(PLAN_NAME.COMFORT);
    const infinity = byName(PLAN_NAME.INFINITY);

    return {
      basic: {
        month: basic?.pricePerMonth ?? DEFAULT_GLOBAL_PRICING.basic.month,
        year: basic?.pricePerYear ?? DEFAULT_GLOBAL_PRICING.basic.year,
      },
      comfort: {
        month: comfort?.pricePerMonth ?? DEFAULT_GLOBAL_PRICING.comfort.month,
        year: comfort?.pricePerYear ?? DEFAULT_GLOBAL_PRICING.comfort.year,
      },
      infinity: {
        month: infinity?.pricePerMonth ?? DEFAULT_GLOBAL_PRICING.infinity.month,
        year: infinity?.pricePerYear ?? DEFAULT_GLOBAL_PRICING.infinity.year,
      },
    };
  };
}

export default TariffPlansStore;
